import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Stack, Chip, Collapse, IconButton,
  Alert, CircularProgress, Divider, Button,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import NotificationsIcon from '@mui/icons-material/Notifications';
import NotificationsOffIcon from '@mui/icons-material/NotificationsOff';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import PeopleIcon from '@mui/icons-material/People';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useNavigate } from 'react-router-dom';
import { tenantApi } from '../../api/tenantApi';

// Map notification type → icon + color + friendly label
const TYPE_META = {
  LOW_STOCK: { icon: <Inventory2Icon fontSize="small" />, color: 'warning', label: 'Low Stock' },
  QUOTA_WARNING: { icon: <WarningAmberIcon fontSize="small" />, color: 'error', label: 'Usage Warning' },
  USER_INVITE: { icon: <PeopleIcon fontSize="small" />, color: 'info', label: 'User Invite' },
  GENERAL: { icon: <InfoOutlinedIcon fontSize="small" />, color: 'default', label: 'Notification' },
};

function metaFor(type) {
  return TYPE_META[type] || { ...TYPE_META.GENERAL, label: type || 'Notification' };
}

function formatWhen(iso) {
  if (!iso) { return ''; }
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) { return ''; }
  const diff = Date.now() - d.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) { return 'just now'; }
  if (mins < 60) { return `${mins}m ago`; }
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) { return `${hrs}h ago`; }
  const days = Math.floor(hrs / 24);
  if (days < 7) { return `${days}d ago`; }
  return d.toLocaleDateString();
}

function NotificationItem({ n }) {
  const [open, setOpen] = useState(false);
  const meta = metaFor(n.type);
  return (
    <Box>
      <Box
        onClick={() => setOpen((o) => !o)}
        sx={{
          cursor: 'pointer', px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 1.5,
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        <Box sx={{ color: meta.color === 'default' ? 'text.secondary' : `${meta.color}.main`, display: 'flex' }}>
          {meta.icon}
        </Box>
        <Box flex={1} minWidth={0}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" fontWeight={600} noWrap>{n.subject || meta.label}</Typography>
            <Chip
              label={meta.label}
              size="small"
              color={meta.color}
              variant="outlined"
              sx={{ fontSize: '0.6rem', height: 18 }}
            />
          </Stack>
          {!open && (
            <Typography variant="caption" color="text.secondary" noWrap display="block">
              {n.body}
            </Typography>
          )}
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
          {formatWhen(n.createdAt)}
        </Typography>
        <IconButton size="small">
          {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </IconButton>
      </Box>
      <Collapse in={open}>
        <Box sx={{ px: 2, pb: 2, pl: 6.5 }}>
          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
            {n.body}
          </Typography>
          {n.createdAt && (
            <Typography variant="caption" color="text.disabled" display="block" mt={1}>
              {new Date(n.createdAt).toLocaleString()}
            </Typography>
          )}
        </Box>
      </Collapse>
      <Divider />
    </Box>
  );
}

export default function NotificationsPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [enabled, setEnabled] = useState(true);

  const load = () => {
    setLoading(true);
    setError(null);
    tenantApi.getMyNotifications()
      .then((data) => {
        setNotifications(data.notifications || []);
        setEnabled(data.notificationsEnabled !== false);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  return (
    <Box sx={{ p: 3, maxWidth: 900 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-end" mb={2}>
        <Box>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <NotificationsIcon sx={{ color: 'primary.main' }} />
            <Typography variant="h5" fontWeight={700}>Notifications</Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            Every notification the system has sent for your account.
          </Typography>
        </Box>
        <Button variant="outlined" size="small" onClick={load} disabled={loading}>Reload</Button>
      </Stack>

      {!enabled && (
        <Alert
          severity="warning"
          icon={<NotificationsOffIcon />}
          sx={{ mb: 2, borderRadius: 2 }}
          action={
            <Button color="inherit" size="small" onClick={() => navigate('/settings')}>
              Enable
            </Button>
          }
        >
          Notifications are turned off. You can re-enable them on your Profile page.
        </Alert>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
        {loading ? (
          <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
        ) : notifications.length === 0 ? (
          <Box textAlign="center" py={6} px={3}>
            <NotificationsIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              No notifications yet. Low-stock alerts, usage warnings, and other system messages will appear here.
            </Typography>
          </Box>
        ) : (
          notifications.map((n) => <NotificationItem key={n.id} n={n} />)
        )}
      </Paper>
    </Box>
  );
}
